"use client";

import { UserRound } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { useAccount } from "@/components/account/AccountProvider";

/**
 * The header's way into the account, in the two shapes the two audiences need.
 *
 * Signed in it is a word — "Your account" — because that is the thing the header is pointing at and
 * no icon says it. It also replaces the designs link (see MyDesignsLink): the account page holds
 * both the orders and the designs, and two nav entries pointing at overlapping lists is one too
 * many on a phone, which is exactly where the labels run out of room.
 *
 * Signed out it is an icon beside the cart, where a shop's sign-in lives. A word there would compete
 * with the create link for width, and signing in is not what a first-time visitor came to do.
 *
 * Renders nothing until /me has answered: a control that changes shape a moment after paint reads
 * as the site forgetting who you are.
 */
export function AccountLink({ className = "" }: { className?: string }) {
  const t = useTranslations("common");
  const { account, loading } = useAccount();

  if (loading) return null;

  if (account) {
    return (
      <Link className={`whitespace-nowrap ${className}`} data-testid="nav-account" href="/account">
        {t("nav.account")}
      </Link>
    );
  }

  return (
    <Link
      aria-label={t("nav.signIn")}
      // Same box and stroke as the cart button next to it, so the two read as one pair.
      className="-mr-1 flex h-9 w-9 items-center justify-center text-ink-soft transition-colors hover:text-ink"
      data-testid="nav-sign-in"
      href="/account/sign-in"
    >
      <UserRound className="h-[18px] w-[18px]" strokeWidth={1.5} />
    </Link>
  );
}
